import React, { useState, useCallback, useEffect, useRef, ReactNode } from "react";

import { ThemeName } from "./constants";
import { getAvailableThemes } from "./helpers/availableThemes";
import { createThemeLinkElement, getCurrentThemeLinkElement } from "./helpers/themeLink";
import { ThemeContext } from "./ThemeContext";
import { FullScreenLoader } from "../components";

const AvailableThemes = getAvailableThemes();
const ThemeNames = AvailableThemes.map(theme => theme.name);
const DefaultTheme = ThemeNames[0];

/**
 * Provides theme context and handles theme switching logic.
 * @param props
 * @param props.children - The child components to render within the ThemeProvider
 * @returns
 */
export const ThemeProvider: React.FC<{
  children: ReactNode;
  loaderOptions?: { loaderOn: boolean; hideLoaderText?: boolean };
}> = ({ children, loaderOptions }) => {
  const linkRef = useRef<HTMLLinkElement | null>(null);

  // Current user theme choice (may be "system" or explicit)
  const [themeSetting, setThemeSetting] = useState<ThemeName>(() => {
    const savedTheme = getThemeFromLocalStorage();
    return savedTheme || DefaultTheme;
  });

  // The resolved theme. Update this to apply a new theme.
  const [resolvedTheme, setResolvedTheme] = useState<ThemeName | null>(null);

  // The actually currently-applied theme. Updates once the theme CSS is loaded.
  const [appliedTheme, setAppliedTheme] = useState<ThemeName | null>(null);

  // On initial mount, figure out what theme (if any) is currently applied
  useEffect(() => {
    const linkElement = getCurrentThemeLinkElement();
    const savedTheme = getThemeFromLocalStorage();

    // if no available saved theme, set to default
    if (!savedTheme) {
      setThemeSetting(DefaultTheme);
      return;
    }

    // if a theme link element exists, check if it matches saved theme
    if (linkElement) {
      const themeName = linkElement.getAttribute("data-theme-name");
      if (themeName && ThemeNames.includes(themeName as ThemeName)) {
        // if applied theme doesn't match user setting, update user setting
        if (themeName !== savedTheme) {
          setThemeSetting(themeName as ThemeName);
        } else {
          // update applied theme directly since we know it's already loaded
          setAppliedTheme(themeName as ThemeName);
        }
        return;
      }
    }
  }, []);

  // On mount and whenever themeSetting changes, resolve and load the new theme
  useEffect(() => {
    setResolvedTheme(resolveTheme(themeSetting));
    setThemeToLocalStorage(themeSetting);
  }, [themeSetting]);

  // Whenever resolved theme changes, load the new theme
  useEffect(() => {
    if (!resolvedTheme || resolvedTheme === appliedTheme) return;

    // Find the theme config
    const theme = AvailableThemes.find(t => t.name === resolvedTheme);
    if (!theme) {
      throw new Error(`Theme "${resolvedTheme}" not found in available themes.`);
    }

    // Get the old link element if any
    const oldLinkElement = linkRef.current;

    // Create the link w/ callback to update state when the theme is loaded
    const newLinkElement = createThemeLinkElement(theme, (loadedThemeName: ThemeName) => {
      // 1. Update the data-theme attribute on the document element
      document.documentElement.setAttribute("data-theme", loadedThemeName);

      // 2. Update applied theme state
      setAppliedTheme(loadedThemeName);

      // 3. Remove the old link element
      if (oldLinkElement && oldLinkElement.parentNode) {
        oldLinkElement.parentNode.removeChild(oldLinkElement);
      }
    });

    // Add the new link element. When loaded, it will trigger the callback above.
    document.head.appendChild(newLinkElement);

    // Update the ref to the current link
    linkRef.current = newLinkElement;
  }, [resolvedTheme]);

  // Listen for OS-level changes when "system" theme is selected.
  // When the user prefers "system", we resolve to either atlas-dark or atlas-light
  // based on their OS preference, and re-resolve whenever that preference changes.
  useEffect(() => {
    if (themeSetting !== "system") return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleSystemThemeChange = () => {
      setResolvedTheme(resolveTheme("system"));
    };

    handleSystemThemeChange();
    mediaQuery.addEventListener("change", handleSystemThemeChange);

    return () => {
      mediaQuery.removeEventListener("change", handleSystemThemeChange);
    };
  }, [themeSetting]);

  // Provide a method to switch themes
  const switchToTheme = useCallback(
    (newTheme: ThemeName): void => {
      if (newTheme === themeSetting) return;

      if (!ThemeNames.includes(newTheme) && newTheme !== "system") {
        console.error(
          `Theme "${newTheme}" is not recognized. Available themes: ${ThemeNames.join(", ")}`
        );
        return;
      }
      setThemeSetting(newTheme);
    },
    [themeSetting]
  );

  // Wait until appliedTheme is set before rendering children
  if (!appliedTheme && loaderOptions?.loaderOn) {
    return (
      <div className="h-screen">
        <FullScreenLoader showText={!loaderOptions?.hideLoaderText} />
      </div>
    );
  }

  return (
    <ThemeContext.Provider
      value={{
        availableThemes: AvailableThemes,
        themeSetting,
        appliedTheme,
        switchToTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Resolves "system" theme to the actual theme based on OS preference.
 * For explicit theme names, returns them unchanged.
 * @param themeName - The theme name (can be "system" or an explicit theme)
 * @returns The resolved theme name ("atlas-dark", "atlas-light", or the explicit theme)
 */
const resolveTheme = (themeName: ThemeName): ThemeName => {
  let resolvedName: ThemeName;
  if (themeName === "system") {
    resolvedName = window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "atlas-dark"
      : "atlas-light";
  } else {
    if (!ThemeNames.includes(themeName as ThemeName)) {
      throw new Error(`Theme "${themeName}" is not recognized.`);
    }
    resolvedName = themeName;
  }
  return resolvedName;
};

/**
 * Get the user's theme choice from localStorage if valid
 * @returns themeName: ThemeName | null
 */
const getThemeFromLocalStorage = (): ThemeName | null => {
  const storedTheme = localStorage.getItem("sj-theme") as ThemeName | null;
  if (storedTheme && (ThemeNames.includes(storedTheme) || storedTheme === "system")) {
    return storedTheme;
  }
  return null;
};

/**
 * Save the user's theme choice to localStorage
 * @param themeName: ThemeName
 */
const setThemeToLocalStorage = (themeName: ThemeName) => {
  localStorage.setItem("sj-theme", themeName);
};
